/* ============================================
   ET - USP Marquee — seamless auto-scrolling USP row.

   The section renders one group of cards; this script clones that group until
   the track is wider than the viewport plus one full cycle, then drives the
   loop with a CSS animation whose distance is exactly one group. Because the
   section is gated behind a body class that an A/B tool may add after load, the
   measuring pass re-runs whenever the row changes size (including 0 -> visible).
   ============================================ */
(function () {
  'use strict';

  var SEL = '[data-section-type="et-usp-marquee"]';
  var MIN_SPEED = 5; // px/s

  function stripEditorAttrs(el) {
    var nodes = [el].concat(Array.prototype.slice.call(el.querySelectorAll('*')));
    nodes.forEach(function (node) {
      if (!node.attributes) return;
      Array.prototype.slice.call(node.attributes).forEach(function (attr) {
        if (attr.name === 'id' || attr.name.indexOf('data-shopify') === 0) {
          node.removeAttribute(attr.name);
        }
      });
    });
  }

  function initSection(section) {
    if (section.__etUspmInit) return;
    section.__etUspmInit = true;

    var viewport = section.querySelector('[data-et-marquee-viewport]');
    var track = section.querySelector('[data-et-marquee-track]');
    var group = section.querySelector('[data-et-marquee-group]');
    if (!viewport || !track || !group) return;

    var speed = parseFloat(section.getAttribute('data-speed'));
    if (!speed || speed < MIN_SPEED) speed = 60;

    var reduce = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false, addListener: null };

    var raf = null;
    var lastShift = 0;

    function removeClones() {
      var clones = track.querySelectorAll('[data-et-marquee-clone]');
      Array.prototype.slice.call(clones).forEach(function (clone) {
        clone.parentNode.removeChild(clone);
      });
    }

    function measure() {
      // One cycle = the distance from a group's left edge to the next group's,
      // which folds in the gap between groups.
      var groups = track.querySelectorAll('[data-et-marquee-group]');
      if (groups.length > 1) {
        return (
          groups[1].getBoundingClientRect().left - groups[0].getBoundingClientRect().left
        );
      }
      return group.getBoundingClientRect().width;
    }

    function layout() {
      section.classList.remove('is-animating');
      removeClones();

      var groupWidth = group.getBoundingClientRect().width;
      var viewportWidth = viewport.getBoundingClientRect().width;

      // Hidden (the test class is not on <body> yet) or fonts/images not in yet.
      if (!groupWidth || !viewportWidth) return;
      if (reduce.matches) return;

      // Enough copies to cover the viewport plus the cycle we scroll away.
      var copies = Math.ceil((viewportWidth + groupWidth) / groupWidth) + 1;
      if (copies < 2) copies = 2;

      var frag = document.createDocumentFragment();
      for (var i = 1; i < copies; i++) {
        var clone = group.cloneNode(true);
        stripEditorAttrs(clone);
        clone.setAttribute('data-et-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        frag.appendChild(clone);
      }
      track.appendChild(frag);

      var shift = measure();
      if (!shift) return;

      lastShift = shift;
      track.style.setProperty('--et-uspm-shift', shift + 'px');
      track.style.setProperty('--et-uspm-duration', shift / speed + 's');
      section.classList.add('is-animating');
    }

    function scheduleLayout() {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        raf = null;
        layout();
      });
    }

    // Icons load after first paint and change the card width.
    Array.prototype.slice.call(section.querySelectorAll('img')).forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', scheduleLayout);
      img.addEventListener('error', scheduleLayout);
    });

    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(scheduleLayout);
    }

    if (typeof ResizeObserver === 'function') {
      // Fires on resize and when the section goes from display:none to visible.
      var ro = new ResizeObserver(function () {
        var width = viewport.getBoundingClientRect().width;
        // Ignore repaints that did not actually change the geometry.
        if (!width) return;
        if (
          section.classList.contains('is-animating') &&
          Math.abs(measure() - lastShift) < 1 &&
          track.getBoundingClientRect().width >= width + lastShift - 1
        ) {
          return;
        }
        scheduleLayout();
      });
      ro.observe(viewport);
    } else {
      window.addEventListener('resize', scheduleLayout);
      // Poll for the test class being added late by the A/B tool.
      var tries = 0;
      var poll = setInterval(function () {
        tries++;
        if (viewport.getBoundingClientRect().width) {
          scheduleLayout();
          clearInterval(poll);
        } else if (tries > 40) {
          clearInterval(poll);
        }
      }, 250);
    }

    if (reduce.addEventListener) {
      reduce.addEventListener('change', scheduleLayout);
    } else if (reduce.addListener) {
      reduce.addListener(scheduleLayout);
    }

    layout();
  }

  function init(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll(SEL)).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) init(e.target);
  });
})();
